import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_IMAGES = [
  { src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_dash-cam-install/artifacts/3dfc74ba6391fd3a_IMG_2438.webp", cat: "installs", label: "Mirror dash cam — front + rear live view" },
  { src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_dash-cam-install/artifacts/f7e224b73e877fc6_IMG_2442.webp", cat: "installs", label: "Mirror display — reverse with park guidelines" },
  { src: "/gallery/blackvue-stealth.webp", cat: "detail", label: "BlackVue 4K UHD — hidden behind the mirror" },
  { src: "/gallery/viofo-a229.webp", cat: "detail", label: "VIOFO A229 Pro — precision fitment" },
  { src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_dash-cam-install/artifacts/e71aa1c7df7b5b4f_IMG_2429.webp", cat: "installs", label: "Wireless rear monitor — work van setup" },
];

const FILTERS = [
  { id: "all", label: "All Work" },
  { id: "installs", label: "Installs" },
  { id: "detail", label: "Detail" },
];

export const photoSrc = (p) => (p.url ? p.url : `${API}/gallery/file/${p.storage_path}`);

const Gallery = () => {
  const [photos, setPhotos] = useState(FALLBACK_IMAGES);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) {
          setPhotos(data.map((p) => ({ src: photoSrc(p), cat: p.cat, label: p.label })));
        }
      })
      .catch(() => {});
  }, []);

  const visible = photos.filter((img) => filter === "all" || img.cat === filter);

  return (
    <section id="work" data-testid="gallery-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <SectionHeading eyebrow="Recent work" title="Proof, not promises" testId="gallery-heading" />

      <div className="flex gap-2 mb-10" data-testid="gallery-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`gallery-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-[background-color,border-color,color] duration-300 ${
              filter === f.id
                ? "bg-sky-500 text-obsidian border-sky-500"
                : "border-white/15 text-gray-400 hover:border-sky-500/50 hover:text-sky-400"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence mode="popLayout">
          {visible.map((img, i) => (
            <motion.button
              layout
              key={img.src}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => setSelected(img)}
              className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-white/10 hover:border-sky-500/40 transition-colors duration-500"
            >
              <img src={img.src} alt={img.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                {img.label}
              </p>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-obsidian/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setSelected(null)}
          >
            <button data-testid="gallery-lightbox-close" className="absolute top-6 right-6 text-gray-400 hover:text-sky-400 transition-colors" aria-label="Close preview">
              <X size={28} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={selected.src} alt={selected.label} className="w-full max-h-[75vh] object-contain rounded-lg border border-white/10" />
              <figcaption className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-sky-400 text-center">
                {selected.label}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
